import { useState } from 'react';
import { IconButton, Tooltip, colors } from '@sweep-io/sweep-design';
import { Copy, Check } from '@sweep-io/sweep-design/dist/icons';
import { telemetry } from '../../../../telemetry';

interface TableCopyButtonProps {
  tableElement: HTMLTableElement;
}

export const TableCopyButton = ({ tableElement }: TableCopyButtonProps) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = async () => {
    try {
      if (!tableElement) {
        telemetry.captureError(new Error('Table element not found for copying'));
        return;
      }

      const htmlContent = tableElement.outerHTML;
      const textContent = tableElement.textContent || tableElement.innerText || '';
      const clipboardData = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
        'text/plain': new Blob([textContent], { type: 'text/plain' }),
      });

      await navigator.clipboard.write([clipboardData]);
      setIsCopied(true);

      // Reset the icon after 2 seconds
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      telemetry.captureError(new Error('Failed to copy table content'), error);
      // Fallback to plain text copy
      try {
        if (tableElement) {
          const textContent = tableElement.textContent || tableElement.innerText || '';
          await navigator.clipboard.writeText(textContent);
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), 2000);
        }
      } catch (fallbackError) {
        telemetry.captureError(new Error('Fallback table copy also failed'), fallbackError);
      }
    }
  };

  return (
    <Tooltip title={isCopied ? 'Copied' : 'Copy table'} placement="top">
      <span>
        <IconButton onClick={handleCopy} size="tiny" variant="flat">
          {isCopied ? <Check color={colors.grey[800]} /> : <Copy />}
        </IconButton>
      </span>
    </Tooltip>
  );
};

